import { create } from "zustand";
import { persist } from "zustand/middleware";
import {
  DEFAULT_CONTENT_STUDIO_STATUS,
  isContentStudioCourseType,
  isContentStudioStatus,
} from "@/features/contentStudio/constants/courseListFilters";
import type {
  ContentStudioCourseType,
  ContentStudioStatus,
} from "@/features/contentStudio/types";

/**
 * Content Studio list page state: active status tab + second-level filters.
 *
 * The filters (status, search, course type, board) are persisted to
 * localStorage so the page re-applies them when the user comes back (route
 * navigation or full reload). The page number is intentionally NOT written to
 * storage, but it does live in this module-level store, so it is kept in
 * memory across in-session route changes (e.g. opening a course and coming
 * back); only a full reload lands on page 1. `authStore.logout()` calls
 * `reset()` so one user's filters never leak to the next.
 */
export interface ContentStudioListState {
  status: ContentStudioStatus;
  /** Committed (already debounced, trimmed) search text. */
  searchQuery: string;
  /** "" = All course types. */
  courseType: ContentStudioCourseType | "";
  /** "" = All boards. Holds the board name. */
  board: string;
  /** 1-based page (not persisted). */
  page: number;
  setPage: (page: number) => void;
  /** Also resets the page to 1. */
  setStatus: (status: ContentStudioStatus) => void;
  /** Also resets the page to 1. */
  setSearchQuery: (query: string) => void;
  /** Also resets the page to 1. */
  setCourseType: (courseType: ContentStudioCourseType | "") => void;
  /** Also resets the page to 1. */
  setBoard: (board: string) => void;
  /** Clears search, course type and board (keeps the status tab); page -> 1. */
  clearFilters: () => void;
  /** Back to the defaults (used on logout). */
  reset: () => void;
}

export const CONTENT_STUDIO_LIST_STORAGE_KEY = "content-studio-list";

const initialState = {
  status: DEFAULT_CONTENT_STUDIO_STATUS,
  searchQuery: "",
  courseType: "" as ContentStudioCourseType | "",
  board: "",
  page: 1,
};

type PersistedFilters = Pick<
  ContentStudioListState,
  "status" | "searchQuery" | "courseType" | "board"
>;

/** Replace unknown / malformed persisted values with the defaults. */
function sanitizePersisted(value: unknown): PersistedFilters {
  const raw =
    value && typeof value === "object"
      ? (value as Partial<Record<keyof PersistedFilters, unknown>>)
      : {};
  return {
    status: isContentStudioStatus(raw.status)
      ? raw.status
      : initialState.status,
    searchQuery:
      typeof raw.searchQuery === "string" ? raw.searchQuery.trim() : "",
    courseType: isContentStudioCourseType(raw.courseType) ? raw.courseType : "",
    board: typeof raw.board === "string" ? raw.board : "",
  };
}

export const useContentStudioListStore = create<ContentStudioListState>()(
  persist(
    (set) => ({
      ...initialState,
      setPage: (page) => set({ page }),
      setStatus: (status) => set({ status, page: 1 }),
      setSearchQuery: (searchQuery) => set({ searchQuery, page: 1 }),
      setCourseType: (courseType) => set({ courseType, page: 1 }),
      setBoard: (board) => set({ board, page: 1 }),
      clearFilters: () =>
        set({ searchQuery: "", courseType: "", board: "", page: 1 }),
      reset: () => set({ ...initialState }),
    }),
    {
      name: CONTENT_STUDIO_LIST_STORAGE_KEY,
      version: 1,
      partialize: (state): PersistedFilters => ({
        status: state.status,
        searchQuery: state.searchQuery,
        courseType: state.courseType,
        board: state.board,
      }),
      merge: (persisted, current) => ({
        ...current,
        ...sanitizePersisted(persisted),
      }),
    },
  ),
);
